import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, withLatestFrom, mergeMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UserInfoService } from './user-info.service';
import { LandingRoutes } from '../components/landing/landing-routes';
import { EmployeeService } from '@app/employee/employee/shared/services/employee.service';
import { SecurityService } from '@app/core/services/security.service';
import { ClientService } from '@app/clients/shared/services/client.service';
import { UserInfoDto } from '../models/user-info-dto';
import { AuthRoutes } from '../components/auth/auth-routes';
import { HttpOperationErrorResponse } from './auth-interceptor';

@Injectable({
	providedIn: 'root',
})
export class AuthGuardService {
	constructor(
		private readonly authService: AuthService,
		private readonly userInfoService: UserInfoService,
		private readonly router: Router,
		private readonly employeeService: EmployeeService,
		private readonly securityService: SecurityService,
		private readonly clientService: ClientService
	) {}

	private redirectToContactMethodRequired(): Promise<boolean> {
		return this.router.navigateByUrl(LandingRoutes.ContactMethodRequired);
	}

	private redirectToUpdateContactInformation(): Promise<boolean> {
		return this.router.navigateByUrl(LandingRoutes.ContactMethodUpdate);
	}

	private redirectToEndImpersonation(): Promise<boolean> {
		return this.router.navigateByUrl(AuthRoutes.EndImpersonation);
	}

	private hasPrimaryContactInfo(userInfo: UserInfoDto): boolean {
		return !!(userInfo?.primaryEmail || userInfo?.primaryPhone);
	}

	private hasProfileContactInfo(): Observable<boolean> {
		return this.employeeService.getUserProfileData().pipe(
			map((profile) => !!(profile?.phoneNumber || profile?.emails?.some((email) => !!email.emailAddress))),
			catchError(() => of(false))
		);
	}

	private isEndImpersonationRoute(url: string): boolean {
		return url === '/' + AuthRoutes.EndImpersonation;
	}

	private validateContactUniqueness(localUserInfo: UserInfoDto, isImpersonating: boolean, resolve: (value: boolean) => void): void {
		const uniqueContactInfoValidated = this.userInfoService.getContactUnique();

		if (uniqueContactInfoValidated === 'false') {
			this.clientService.getClientSettings(localUserInfo.clientId).subscribe({
				next: (clientSettings) => {
					if (clientSettings.allowDuplicateEmails) {
						resolve(true);
					} else {
						this.contactUpdate().subscribe((result) => {
							if (result === false) {
								this.redirectToUpdateContactInformation();
								resolve(false);
							} else {
								resolve(true);
							}
						});
					}
				},
				error: (error: HttpOperationErrorResponse) => {
					if (isImpersonating) {
						sessionStorage.setItem('impersonation-error', `${localUserInfo.firstName} ${localUserInfo.lastName}`);
						this.redirectToEndImpersonation();
						resolve(false);
					} else {
						this.redirectToUpdateContactInformation();
						resolve(false);
					}
				},
			});
		} else {
			resolve(true);
		}
	}

	public canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
		var userValidated: Promise<boolean> = new Promise((resolve) => {
			(this.userInfoService.userInfo
				.pipe(
					withLatestFrom(this.authService.isLoggedIn, this.authService.isImpersonating),
					map(([userInfo, isLoggedIn, isImpersonating]) => {
						// Anonymous user scenario
						if (!isLoggedIn) {
							this.authService.startAuthenticationAsync(state.url);
							resolve(false);
							return;
						}

						// Note: Need to reference local storage due to how the OIDC observable is handled and how that impacts the userInfo observable.
						let localUserInfo = this.userInfoService.getUserInfoFromStorage();

						// Incomplete security information scenario
						if (this.isEndImpersonationRoute(state.url)) {
							resolve(true);
							return;
						}

						if (!this.hasPrimaryContactInfo(localUserInfo)) {
							this.hasProfileContactInfo().subscribe((hasProfileContactInfo) => {
								if (!hasProfileContactInfo && state.url != LandingRoutes.ContactMethodRequired) {
									state.url = LandingRoutes.ContactMethodRequired;
									this.redirectToContactMethodRequired();
									resolve(false);
									return;
								}

								this.validateContactUniqueness(localUserInfo, isImpersonating, resolve);
							});
						} else {
							this.validateContactUniqueness(localUserInfo, isImpersonating, resolve);
						}
					})
				)
				.subscribe(
					(result) => {},
					() => {
						resolve(true); // error
					}
				),
				() => {
					resolve(true); // completed
				});
		});
		return userValidated;
	}

	private contactUpdate(): Observable<boolean> {
		let emailCompany: string;
		let emailPersonal: string;
		let phoneNumber: string;

		return this.employeeService.getUserProfileData().pipe(
			// Check if Company Email is unique
			mergeMap((result) => {
				emailCompany =
					result.emails.find((value) => value.emailTypeId === 'Company') &&
					result.emails.find((value) => value.emailTypeId === 'Company').emailAddress;

				emailPersonal =
					result.emails.find((value) => value.emailTypeId === 'Personal') &&
					result.emails.find((value) => value.emailTypeId === 'Personal').emailAddress;

				phoneNumber = result.phoneNumber;

				return this.employeeService.isEmailUnique(emailCompany);
			}),

			// Check if Personal Email is unique
			mergeMap((result) => {
				if (result === true) {
					return this.employeeService.isEmailUnique(emailPersonal);
				}
				return of(false);
			}),

			// Check if Phone Number is unique
			mergeMap((result) => {
				if (result === true) {
					return this.securityService.isPhoneNumberUnique(phoneNumber);
				}
				return of(false);
			}),

			// Return result and set local storage flag
			mergeMap((result) => {
				this.userInfoService.setContactUnique(result ? 'true' : 'false');
				return of(result);
			})
		);
	}
}
